import axios from 'axios'
import { StdFee } from '@cosmjs/amino'
import { SignerData, DeliverTxResponse } from '@cosmjs/stargate'
import { EncodeObject, isOfflineDirectSigner } from '@cosmjs/proto-signing'
import { TxRaw } from 'cosmjs-types/cosmos/tx/v1beta1/tx'
import { generateEndpointBroadcast, generatePostBodyBroadcast } from '@evmos/provider'
import { createTxRaw } from '@evmos/proto'
import { createTxIBCMsgTransfer } from '@evmos/transactions'
import { EthSignType } from '@keplr-wallet/types'
import { Buffer } from 'buffer'
import { EVMOS_CHAIN_INFO } from '@/config'
import { fatal, poll } from '@/utils'
import { broadcastTx, convertDenomToMicroDenom } from '../utils'
import { TxQueryResponse } from '../types'
import { EvmosIbcReturnType } from './signer-types'
import { IbcSignerParams } from './types'

const EVMOS_SUPPORTED_WALLET_TYPES = ['keplr-extension', 'leap-extension', 'cosmostation-extension']

// These are the recommended functions to sign and broadcast
// ibc transactions that could come from Evmos to Stride.

interface BroadcastResponse {
  tx_response: {
    height: string
    code: number
    txhash: string
    raw_log: string
    data: string
    gas_used: string
    gas_wanted: string
    log: string
    codespace: string
  }
}

const QUERY_TRANSACTION_INTERVAL = 3000

const QUERY_TRANSACTION_ATTEMPTS = 20

const QUERY_TRANSACTION_ATTEMPT_MS = QUERY_TRANSACTION_INTERVAL * QUERY_TRANSACTION_ATTEMPTS

// Almost 1:1 API parity with `SigningStargateClient.sign`
//
// v2 using cosmos-kit primitives instead of Account abstraction
// @TODO: Rename to signEvmosIbcTransaction2 - we previously kept the original function to keep things working
// as we refactor it out (this one accepts slightly different types to accommodate for the wallet rework)
const signEvmosIbcTransaction2 = async (
  payload: IbcSignerParams,
  address: string,
  messages: EncodeObject[],
  fee: StdFee,
  memo: string,
  _explicitSignerData?: SignerData
): Promise<EvmosIbcReturnType> => {
  const { accountNumber, sequence } = await payload.client.getSequence(address)

  const message = messages[0]?.value

  if (message == null) {
    throw fatal('Unable to sign an ibc transaction without any messages.')
  }

  const feeAmount = fee.amount[0]

  if (feeAmount == null) {
    throw fatal('Unable to sign an ibc transaction without a fee amount.')
  }

  const payloadFee = {
    amount: isOfflineDirectSigner(payload.offlineSigner)
      ? feeAmount.amount
      : String(convertDenomToMicroDenom(0.05, feeAmount.denom)),
    denom: feeAmount.denom,
    gas: fee.gas
  }

  const chain = {
    chainId: 9001,
    cosmosChainId: EVMOS_CHAIN_INFO.chainId
  }

  const sender = {
    accountAddress: address,
    sequence,
    accountNumber,
    pubkey: Buffer.from(payload.account.pubkey).toString('base64')
  }

  const { signDirect, eipToSign } = createTxIBCMsgTransfer(
    {
      chain,
      fee: payloadFee,
      sender,
      memo
    },
    {
      sourcePort: (message as any).sourcePort,
      sourceChannel: (message as any).sourceChannel,
      amount: (message as any).token.amount,
      denom: (message as any).token.denom,
      receiver: (message as any).receiver,
      timeoutTimestamp: String((message as any).timeoutTimestamp),
      revisionNumber: 9001,
      revisionHeight: Number.MAX_SAFE_INTEGER
    }
  )

  if (isOfflineDirectSigner(payload.offlineSigner)) {
    const { signature, signed } = await payload.offlineSigner.signDirect(address, {
      bodyBytes: signDirect.body.toBinary(),
      authInfoBytes: signDirect.authInfo.toBinary(),
      accountNumber: BigInt(accountNumber),
      chainId: EVMOS_CHAIN_INFO.chainId
    })

    return {
      type: 'evmos',
      value: createTxRaw(signed.bodyBytes, signed.authInfoBytes, [
        new Uint8Array(Buffer.from(signature.signature, 'base64'))
      ])
    }
  }

  if (!EVMOS_SUPPORTED_WALLET_TYPES.includes(payload.wallet))
    throw fatal(`Wallet type ${payload.wallet} does not support Evmos signing.`)

  let walletInstance

  switch (payload.wallet) {
    case 'keplr-extension':
      walletInstance = window.keplr
      break
    case 'leap-extension':
      walletInstance = window.leap
      break
    case 'cosmostation-extension':
      walletInstance = window.cosmostation.providers.keplr
      break
  }

  if (!walletInstance) throw fatal(`Could not find wallet with Evmos signing support.`)

  const signature = await walletInstance.signEthereum(
    EVMOS_CHAIN_INFO.chainId,
    address,
    JSON.stringify(eipToSign),
    EthSignType.EIP712
  )

  return {
    type: 'evmos-ledger',
    value: createTxRaw(signDirect.body.toBinary(), signDirect.authInfo.toBinary(), [signature])
  }
}

// Almost 1:1 API parity with StargateClient.broadcastTx
// Here, we also re-implemented Stargate's broadcasting function fitted for Evmos' broadcasting method.
// First, we'll try to broadcast the transaction. Second, we'll do a series of checks, then we'll poll
// the transaction until it's there. Otherwise, we timeout after 60 seconds.
// @TODO: Implement standard tx error (via `assertTxSuccess`)
//
// @TODO: Rename to broadcastEvmosIbcTransaction2 - we previously kept the original function to keep things working
// as we refactor it out (this one accepts slightly different types to accommodate for the wallet rework)
const broadcastEvmosIbcTransaction2 = async (
  payload: IbcSignerParams,
  signed: EvmosIbcReturnType
): Promise<DeliverTxResponse> => {
  if (signed.type === 'evmos') {
    const bytes = TxRaw.encode(signed.value.message).finish()
    return broadcastTx(payload.client, bytes)
  }

  const instance = axios.create({ baseURL: EVMOS_CHAIN_INFO.rest })

  const broadcastResponse = await instance.post<BroadcastResponse>(
    generateEndpointBroadcast(),
    JSON.parse(generatePostBodyBroadcast(signed.value, 'BROADCAST_MODE_SYNC'))
  )

  const { code, raw_log, codespace, txhash } = broadcastResponse.data.tx_response

  // First, we'll exit early if the transaction code is not 0.
  if (broadcastResponse.data.tx_response.code) {
    throw new Error(`Broadcasting transaction failed with code ${code} (codespace: ${codespace}). Log: ${raw_log}`)
  }

  const transactionParameters = new URLSearchParams()
  transactionParameters.append('events', `tx.hash='${txhash}'`)

  const pollFn = async () => {
    try {
      const transactionResponse = await instance.get<TxQueryResponse>(`cosmos/tx/v1beta1/txs?${transactionParameters}`)
      if (transactionResponse.data.tx_responses.length === 0) return null
      return transactionResponse.data.tx_responses[0]
    } catch (e) {
      // @TODO: Check for BroadcastTxError like we're doing with `broadcastTx`
      // https://github.com/Stride-Labs/interface/issues/535
      if (process.env.NODE_ENV === 'development') {
        console.error(e)
      }
      return null
    }
  }

  // broadcastResponse is unlikely to contain the transaction data.
  // We'll poll for the transaction data for one minute.
  const transaction = await poll(pollFn, Boolean, {
    ms: 3000,
    max: 20
  })

  // If transaction is still null, it's likely it's timed out.
  if (transaction == null) {
    throw new Error(
      `Transaction with hash ${txhash} was submitted but was not yet found on the chain. You might want to check later. There was a wait of ${
        QUERY_TRANSACTION_ATTEMPT_MS / 1000
      } seconds.`
    )
  }

  return {
    height: Number(transaction.height),
    code: Number(transaction.code),
    transactionHash: transaction.txhash,
    gasUsed: BigInt(transaction.gas_used),
    gasWanted: BigInt(transaction.gas_wanted),
    rawLog: transaction.raw_log,
    events: transaction.events,
    // @TODO: Look into adding this to the response so we're consistent
    txIndex: 0,
    // @TODO: Look into adding this to the response so we're consistent
    msgResponses: []
  }
}

export { signEvmosIbcTransaction2, broadcastEvmosIbcTransaction2 }
